/**
 * The one-line description of what the TTs & Service Failures report is
 * currently holding.
 *
 * The filters are easy to read past — especially Status, which defaults to
 * Resolved only. That default is a single word in a crowded filter bar, it
 * does not travel with an Excel export, and V1's trouble-ticket report (the
 * thing people reconcile this against for the monthly numbers) defaults to
 * every status. Spelling the scope out under the title is what makes a short
 * count explainable without re-running anything.
 */

export interface ScopeSubtitleArgs {
  /** Label of the selected date basis, e.g. "Created". */
  dateBasisLabel: string;
  startDate: Date | null;
  endDate: Date | null;
  /** Selected status values. */
  statuses: string[];
  /** Every status the filter offers, as { value, label }. */
  statusOptions: { value: string; label: string }[];
  lcs: string[];
  regions: string[];
}

/** M/D/YYYY — matches how the date pickers above the grid read. */
const formatDate = (date: Date): string =>
  date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });

const countOf = (items: string[], one: (v: string) => string, many: (n: number) => string, all: string): string => {
  if (items.length === 0) return all;
  if (items.length === 1) return one(items[0]);
  return many(items.length);
};

export function buildScopeSubtitle({
  dateBasisLabel,
  startDate,
  endDate,
  statuses,
  statusOptions,
  lcs,
  regions,
}: ScopeSubtitleArgs): string {
  const range = startDate && endDate ? `${formatDate(startDate)}–${formatDate(endDate)}` : 'no date range';

  // An empty selection means "all" to the endpoint, and so does having ticked
  // every box — both have to read as "all statuses" or the line lies.
  const everyStatus = statuses.length === 0 || statusOptions.every((o) => statuses.includes(o.value));
  const statusText = everyStatus
    ? 'all statuses'
    : statusOptions
        .filter((o) => statuses.includes(o.value))
        .map((o) => o.label)
        .join(', ');

  const lcText = countOf(lcs, (v) => `LC ${v}`, (n) => `${n} LCs`, 'all LCs');
  const regionText = countOf(regions, (v) => v, (n) => `${n} regions`, 'all regions');

  return `${dateBasisLabel} ${range} · ${statusText} · ${lcText} · ${regionText}`;
}
